<template>
  <div
    ref="refBuilding"
    :class="{
      'pointer-events-none': placing,
      'culture-bonus': building.hasCultureBonus,
      'opacity-50': !building.hasCultureBonus && !building.effects.getHappiness() && displayMode !== 'normal',
    }"
    :style="myStyle"
    class="building absolute select-none z-10 placed"
    @click="onClicked"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="cont w-full h-full flex flex-col items-center justify-center bg-black">
      <BuildingImageDisplay
        :template-id="building.template.id"
        :happiness-level="thisBuilding.happiness ? thisBuilding.happiness.level : 0"
        :has-pending="placing"
        :rotated="thisBuilding.isRotated"
      />

      <div
        v-if="isMouseOver && building.hasCultureBonus && currentCultureBonus > 0 && displayMode === 'normal' && building.width > 1 && building.height > 1"
        class="absolute left-1 bottom-1"
      >
        <img
          :src="`${require('@/assets/images/happiness2.png')}`"
          alt=""
          :class="{
            'w-3 h-3': building.width === 1,
            'w-4 h-4': building.width === 2,
            'w-5 h-5': building.width >= 3,
          }"
        />
      </div>

      <span
        v-if="displayMode !== 'normal' && thisBuilding.happiness !== null && showBonusHappiness"
        class="text-white text-center leading-tight text-xxs"
      >
        {{ thisBuilding.happiness.value }}<br>({{ thisBuilding.happiness.required }})
      </span>

      <div
        v-if="editable"
        class="mover absolute -right-2 -top-2 leading-none cursor-move text-white bg-black border border-white rounded-full"
        title="Move"
        @click.stop="onMoverClicked"
      >
        <i class="fas fa-arrows-alt pointer-events-none"/>
      </div>

      <Teleport to="body">
        <BuildingTooltip
          v-if="showTooltip"
          :building-id="buildingId"
          @era="changeEra"
          @hide="hideTooltip"
          @level="changeLevel"
        />
      </Teleport>
    </div>
  </div>
</template>

<script>
import Grid from '@/roc/grid/Grid';
import BuildingImageDisplay from '@/components/Grid/PlacedBuilding/BuildingImageDisplay.vue';
import BuildingTooltip from '@/components/Grid/PlacedBuilding/BuildingTooltip.vue';
import {computed, nextTick, onBeforeUnmount, onMounted, readonly, ref, watch} from 'vue';
import {useApplicationStore} from '@/roc/stores/ApplicationStore';
import {useLayoutStore} from '@/roc/stores/LayoutStore';
import {useMuseumStore} from '@/roc/stores/MuseumStore';

export default {
  components: {
    BuildingTooltip,
    BuildingImageDisplay
  },
  props: {
    placing: {
      type: Boolean,
      required: true
    },
    buildingId: {
      type: String,
      required: true
    }
  },
  emits: ['relocate', 'era', 'level', 'ready'],
  setup (props, context) {
    const appStore = useApplicationStore();
    const lytStore = useLayoutStore();
    const museumStore = useMuseumStore();

    const thisBuilding = computed(() => {
      return lytStore.buildings[props.buildingId];
    });

    /** @type {Ref<Number>} */
    const buildingWidth = ref(thisBuilding.value.isRotated ? thisBuilding.value.height : thisBuilding.value.width);
    /** @type {Ref<Number>} */
    const buildingHeight = ref(thisBuilding.value.isRotated ? thisBuilding.value.width : thisBuilding.value.height);
    const showTooltip = ref(false);
    const currentCultureBonus = ref(null);
    const isMouseOver = ref(false);

    /** @type {Ref<HTMLDivElement>} */
    const refBuilding = ref(null);

    let updateInterval = null;

    const updateCultureStats = () => {
      if (!thisBuilding.value || !thisBuilding.value.hasCultureBonus) {
        return;
      }

      const bonus = thisBuilding.value.effects.getCulture(thisBuilding.value.level, thisBuilding.value.era);

      refBuilding.value.dataset.buildingCultureBonus = bonus.value.toString();
      refBuilding.value.dataset.buildingCultureBonusRange = bonus.range.toString();

      currentCultureBonus.value = bonus.value;

      /** @type {HTMLDivElement} */
      const marker = document.querySelector('[data-marked-building="' + props.buildingId + '"]');

      marker.style.left = (refBuilding.value.style.left.replace('px', '') - bonus.range * Grid.CELL_SIZE) + 'px';
      marker.style.top = (refBuilding.value.style.top.replace('px', '') - bonus.range * Grid.CELL_SIZE) + 'px';
      marker.style.width = (refBuilding.value.clientWidth + bonus.range * Grid.CELL_SIZE * 2) + 'px';
      marker.style.height = (refBuilding.value.clientHeight + bonus.range * Grid.CELL_SIZE * 2) + 'px';
    };

    onBeforeUnmount(() => {
      if (updateInterval !== null) {
        clearInterval(updateInterval);
        updateInterval = null;
      }
    });

    onMounted(() => {
      const i = setInterval(() => {
        if (refBuilding.value) {
          refBuilding.value.dataset.buildingId = thisBuilding.value.templateId;
          refBuilding.value.dataset.buildingReference = props.buildingId;

          updateCultureStats();
          clearInterval(i);

          nextTick(() => {
            context.emit('ready', props.buildingId);
          });
        }
      }, 5);
    });

    const thisBuildingLevel = computed(() => {
      return thisBuilding.value ? thisBuilding.value.level : 0;
    });
    const thisBuildingEra = computed(() => {
      return thisBuilding.value ? thisBuilding.value.era : '';
    });
    const museumCultureBonus = computed(() => {
      return museumStore.cultureBonus;
    });

    watch ([thisBuildingLevel, thisBuildingEra, museumCultureBonus], () => {
      updateCultureStats();
      thisBuilding.value.calculateHappiness();
    });

    return {
      showTooltip,
      currentCultureBonus,
      isMouseOver,
      thisBuilding,

      refBuilding,
      building: readonly(thisBuilding),

      myStyle: computed(() => {
        return [
          'left:' + (thisBuilding.value.x * Grid.CELL_SIZE) + 'px',
          'top:' + (thisBuilding.value.y * Grid.CELL_SIZE) + 'px',
          'width:' + (buildingWidth.value * Grid.CELL_SIZE + 1) + 'px',
          'height:' + (buildingHeight.value * Grid.CELL_SIZE + 1) + 'px'
        ].join(';');
      }),

      editable: computed(() => {
        return appStore.editable;
      }),

      showBonusHappiness: computed(() => {
        return appStore.showBonusHappiness;
      }),

      displayMode: computed(() => {
        return appStore.displayMode;
      }),

      onMoverClicked() {
        context.emit('relocate', props.buildingId);
      },

      onClicked() {
        showTooltip.value = true;

        if (thisBuilding.value.hasCultureBonus) {
          const marker = document.querySelector('[data-marked-building="' + props.buildingId + '"]');
          marker.classList.add('hidden');
        }
      },

      onMouseEnter() {
        isMouseOver.value = true;

        if (thisBuilding.value.hasCultureBonus && currentCultureBonus.value > 0) {
          const marker = document.querySelector('[data-marked-building="' + props.buildingId + '"]');
          marker.classList.remove('hidden');
        }
      },

      onMouseLeave() {
        isMouseOver.value = false;

        if (thisBuilding.value.hasCultureBonus && currentCultureBonus.value > 0) {
          const marker = document.querySelector('[data-marked-building="' + props.buildingId + '"]');
          marker.classList.add('hidden');
        }
      },

      changeEra(newEraId) {
        context.emit('era', {
          building: props.buildingId,
          era: newEraId
        });
      },

      changeLevel(newLevel) {
        context.emit('level', {
          building: props.buildingId,
          level: newLevel
        });
      },

      hideTooltip() {
        showTooltip.value = false;
      }
    };
  }
};
</script>

<style scoped>
.building {
  transition: all 50ms ease-in;
  background-color: lightgreen;
  padding: 1px;
}

.building .mover {
  transition: all 200ms ease-in;
  opacity: 0;
  padding: 2px;
}

.building:hover {
  z-index: 100;
  padding: 3px;
}

.building:hover .mover {
  opacity: 1;
}

.building.emphasize {
  background-color: red;
  z-index: 101;
}

.trans {
  transition: all 200ms ease-in-out;
}
</style>
